import {
  Box,
  Flex,
  Text,
  HStack,
  IconButton,
  Dialog,
} from '@chakra-ui/react';
import { MdRefresh as RefreshIcon, MdClose as CloseIcon, MdHelpOutline as HelpIcon } from 'react-icons/md';
import { useAIChatController } from '@/hooks/useAIChatController';
import ChatConversation from './ChatConversation';

interface AIChefChatProps {
  onClose?: () => void;
  variant?: 'modal' | 'widget';
}

export default function AIChefChat({
  onClose,
  variant = 'modal',
}: AIChefChatProps) {
  const {
    recipeId,
    messages,
    isLoading,
    currentRecipe,
    currentCollection,
    pendingAction,
    confirmAction,
    cancelAction,
    isConfirming,
    helpMode,
    resetChat,
    handleHelp,
    onSend,
  } = useAIChatController();

  const isWidget = variant === 'widget';

  return (
    <Dialog.Root
      open={true}
      onOpenChange={(details) => !details.open && onClose && onClose()}
    >
      <Dialog.Backdrop bg={isWidget ? 'transparent' : 'blackAlpha.600'} />
      <Dialog.Positioner
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={10000}
        display="flex"
        alignItems={isWidget ? 'flex-end' : 'center'}
        justifyContent={isWidget ? 'flex-end' : 'center'}
        p={isWidget ? 4 : 0}
        pointerEvents={isWidget ? 'none' : 'auto'}
      >
        <Dialog.Content
          maxH={isWidget ? '600px' : '90vh'}
          h={isWidget ? '600px' : { base: '90vh', md: '80vh' }}
          w={isWidget ? { base: 'calc(100vw - 32px)', md: '400px' } : 'auto'}
          maxW={isWidget ? { base: 'calc(100vw - 32px)', md: '400px' } : 'auto'}
          borderRadius="xl"
          overflow="hidden"
          mx={isWidget ? 0 : { base: 2, md: 0 }}
          boxShadow="0 24px 64px rgba(0,0,0,0.35)"
          display="flex"
          flexDirection="column"
          bg="bg.surface"
          border="1px solid"
          borderColor="border.muted"
          borderTop="3px solid"
          borderTopColor="accent.secondary"
          pointerEvents="auto"
        >
          <Dialog.Header
            px={3}
            py={2}
            bg="bg.canvas"
            borderBottom="1px solid"
            borderBottomColor="border.muted"
            flexShrink={0}
          >
            <Flex align="center" w="100%" gap={2}>
              <Box w="28px" h="28px" flexShrink={0}>
                <img src="/favicon.png" alt="" width={28} height={28} style={{ display: 'block', objectFit: 'contain' }} />
              </Box>
              <HStack gap={1.5} flex={1} minW={0} overflow="hidden">
                <Text fontFamily="heading" fontWeight="800" fontSize="md" color="text.primary" flexShrink={0}>
                  Chef Assistant
                </Text>
                {(currentRecipe?.title ?? currentCollection?.title) && (
                  <Text fontSize="sm" color="accent.secondary" fontWeight="500" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    · {currentRecipe?.title ?? currentCollection?.title}
                  </Text>
                )}
              </HStack>
              <HStack gap={1}>
                <IconButton
                  aria-label="Help"
                  size="sm"
                  variant="ghost"
                  color={helpMode ? 'accent.secondary' : 'text.muted'}
                  onClick={handleHelp}
                >
                  <HelpIcon />
                </IconButton>
                <IconButton
                  aria-label="Reset chat"
                  size="sm"
                  variant="ghost"
                  color="text.muted"
                  onClick={resetChat}
                  disabled={messages.length === 0}
                >
                  <RefreshIcon />
                </IconButton>
                <IconButton
                  aria-label="Close"
                  size="sm"
                  variant="ghost"
                  color="text.muted"
                  onClick={() => onClose?.()}
                >
                  <CloseIcon />
                </IconButton>
              </HStack>
            </Flex>
          </Dialog.Header>

          <Dialog.Body p={0} flex="1" minH="0" display="flex" flexDirection="column">
            <ChatConversation
              messages={messages}
              isLoading={isLoading}
              onSend={onSend}
              recipeId={recipeId}
              currentRecipe={currentRecipe}
              currentCollection={currentCollection}
              inputPlaceholder="Message"
              helpMode={helpMode}
              pendingAction={pendingAction}
              onConfirmAction={confirmAction}
              onCancelAction={cancelAction}
              isConfirmingAction={isConfirming}
            />
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
